/* eslint-disable @typescript-eslint/no-explicit-any */

import { useAtom } from 'jotai';
import {
  useMutation,
  UseMutationOptions,
  useQuery,
  UseQueryOptions,
  UseQueryResult,
} from '@tanstack/react-query';
import { LoginForm, NewProjectForm, NewReportForm, NewUserForm, RefreshTokenForm } from './schemas';
import {
  ForgotPassword,
  ListParamsProject,
  ListParamsReport,
  ListParamsUser,
  NewPassword,
  Project,
  ResetPassword,
} from '../types';
import { ApiClient, getAuthLocal, setAuthLocal } from './api';
import { QUERY_KEYS, USER_ROLE } from './constants';
import { mutationLoadingAtom } from '../atoms/mutationLoadingAtom';

const apiClient = new ApiClient();

function useGlobalQuery<
  TQueryFnData = unknown,
  TError = unknown,
  TData = TQueryFnData,
  TQueryKey extends readonly unknown[] = [],
>(
  queryKey: TQueryKey,
  queryFn: () => Promise<TQueryFnData>,
  options?: Omit<
    Omit<UseQueryOptions<TQueryFnData, TError, TData, TQueryKey>, 'queryFn'>,
    'queryKey'
  >,
): UseQueryResult<TData, TError> {
  return useQuery({
    ...options,
    queryKey,
    queryFn,
  });
}

function useGlobalMutation<
  TData = unknown,
  TError = unknown,
  TVariables = void,
  TContext = Record<string, unknown>,
>(
  mutationFn: (variables: TVariables) => Promise<TData>,
  options?: UseMutationOptions<TData, TError, TVariables, TContext>,
) {
  const [, setLoading] = useAtom(mutationLoadingAtom);

  const mutation = useMutation({
    ...options,
    mutationFn,
    onMutate: async (variables) => {
      setLoading(true);
      if (options?.onMutate) {
        return options.onMutate(variables);
      }
      return undefined;
    },
    onSettled: async (data, error, variables, context) => {
      setLoading(false);
      if (options?.onSettled) {
        await options.onSettled(data, error, variables, context);
      }
    },
    onError: async (error, variables, context) => {
      setLoading(false);
      if (options?.onError) {
        await options.onError(error, variables, context);
      }
    },
    onSuccess: async (data, variables, context) => {
      setLoading(false);
      if (options?.onSuccess) {
        await options.onSuccess(data, variables, context);
      }
    },
  });

  return mutation;
}

export const useProjectsQuery = (params?: ListParamsProject) => {
  return useGlobalQuery([QUERY_KEYS.PROJECTS, params], () => apiClient.getUserProjects(params), {
    throwOnError: false,
    retry: false,
  });
};

export const useReportsQuery = (params: ListParamsReport, enabled: boolean) => {
  return useGlobalQuery([QUERY_KEYS.REPORTS, params], () => apiClient.getReportsByProject(params), {
    enabled,
  });
};

export const useStatusesQuery = (projectId: string) => {
  return useGlobalQuery(
    [QUERY_KEYS.STATUSES, projectId],
    () => apiClient.getStatusesByProjectId(projectId),
    { enabled: !!projectId, retry: false },
  );
};

export const useReportQuery = (id: string) => {
  return useGlobalQuery([QUERY_KEYS.REPORT, id], () => apiClient.getReportById(id), {
    retry: false,
  });
};

export const useLoginMutation = () => {
  return useGlobalMutation((params: LoginForm) => apiClient.login(params), {
    onSuccess: (data) => {
      setAuthLocal({
        user: null,
        accessToken: data.accessToken,
        refreshToken: data.refreshToken,
      });
    },
  });
};

export const useValidateSessionMutation = () => {
  return useGlobalMutation(() => apiClient.validate(), {
    onSuccess: (data) => {
      const auth = getAuthLocal();
      setAuthLocal({ ...auth, user: data });
    },
    onError: (error: any) => {
      if (error.cause?.status === 401) {
        setAuthLocal({
          user: null,
          accessToken: null,
          refreshToken: null,
        });
      }
    },
  });
};

export const useRefreshTokenMutation = () => {
  return useGlobalMutation((params: RefreshTokenForm) => apiClient.refresh(params), {
    onSuccess: (data) => {
      const auth = getAuthLocal();
      setAuthLocal({
        user: auth?.user || null,
        accessToken: data.accessToken,
        refreshToken: data.refreshToken,
      });
    },
    onError: (error: any) => {
      if (error.cause?.status === 401) {
        setAuthLocal({
          user: null,
          accessToken: null,
          refreshToken: null,
        });
      }
    },
  });
};

export const useCreateReportMutation = () => {
  return useGlobalMutation(
    async (reportForm: NewReportForm & { project: string }) => {
      const expectedScreenshots = reportForm.expectedScreenshots
        ? await Promise.all(
            reportForm.expectedScreenshots.map(async (currentScreenshot) => {
              const { screenshot } = await apiClient.createScreenshot({ file: currentScreenshot });

              return screenshot.id;
            }),
          )
        : [];

      const currentScreenshots = reportForm.currentScreenshots
        ? await Promise.all(
            reportForm.currentScreenshots.map(async (currentScreenshot) => {
              const { screenshot } = await apiClient.createScreenshot({ file: currentScreenshot });

              return screenshot.id;
            }),
          )
        : [];

      const newReport = {
        ...reportForm,
        expectedScreenshots,
        currentScreenshots,
      };

      return apiClient.createReport(newReport);
    },
    {
      throwOnError: false,
    },
  );
};

export const useChangeReportStatusMutation = () => {
  return useGlobalMutation((params: { id: string; status: string }) =>
    apiClient.changeReportStatus(params.id, params.status),
  );
};

export const useCreateUserMutation = () => {
  return useGlobalMutation((user: Omit<NewUserForm, 'project'> & { project: string[] }) =>
    apiClient.registrateUser(user),
  );
};

export const useUsersQuery = (params: ListParamsUser) => {
  return useGlobalQuery([QUERY_KEYS.USERS, params], () => apiClient.getUsers(params));
};

export const useCreateProjectMutation = () => {
  return useGlobalMutation((project: Omit<NewProjectForm, 'users'> & { users: string[] }) =>
    apiClient.createProject(project),
  );
};

export const useChangeProjectStatusMutation = () => {
  return useGlobalMutation((params: { project: Project; status: string }) =>
    apiClient.changeProjectStatus(params.project, params.status),
  );
};

export const useProjectQuery = (pid: string) => {
  return useGlobalQuery([QUERY_KEYS.PROJECT, pid], () => apiClient.getProjectById(pid));
};

export const useDeleteReportMutation = () => {
  return useMutation({ mutationFn: (id: string) => apiClient.deleteReport(id) });
};

export const useChangePasswordMutation = () => {
  return useGlobalMutation((change: NewPassword) => apiClient.changePassword(change));
};

export const useForgotPasswordMutation = () => {
  return useGlobalMutation((email: ForgotPassword) => apiClient.forgotPassword(email));
};

export const useResetPasswordMutation = () => {
  return useGlobalMutation((reset: ResetPassword) => apiClient.resetPassword(reset));
};

export const useClientsQuery = (params: ListParamsUser) => {
  return useGlobalQuery([QUERY_KEYS.CLIENTS, params], () =>
    apiClient.getUsers({ ...params, filters: { role: { $in: [USER_ROLE.CLIENT] } } }),
  );
};

export const useUpdateProjectUsersMutation = () => {
  return useGlobalMutation((params: { pid: string; users: string[] }) =>
    apiClient.updateProjectUsers(params),
  );
};

export const useUserQuery = (uid: string) => {
  return useGlobalQuery([QUERY_KEYS.USER, uid], () => apiClient.getUserById(uid));
};

export const useUpdateLanguageMutation = () => {
  return useGlobalMutation((language: string) => apiClient.updateLanguage(language));
};
